import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBriefcase,
  FaCalendarAlt,
  FaEdit,
  FaInfoCircle,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { canDeleteInPortal } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const EMPLOYEE_STATUSES = ["Active", "Inactive", "Resigned", "Terminated", "On Hold"];

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return displayValue(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function initials(row) {
  const first = (row?.First_Name || "").trim().charAt(0);
  const last = (row?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "E";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive" || s === "terminated" || s === "resigned") {
    return "cportal-badge cportal-badge--danger";
  }
  return "cportal-badge";
}

function fullName(row) {
  return `${(row?.First_Name || "").trim()} ${(row?.Last_Name || "").trim()}`.trim() || "—";
}

function phoneDisplay(row) {
  const cc = row?.Country_Code ? String(row.Country_Code).trim() : "";
  const phone = row?.Phone_Number ? String(row.Phone_Number).trim() : "";
  if (!phone) return "—";
  return cc ? `${cc} ${phone}` : phone;
}

function toForm(row) {
  return {
    firstName: row?.First_Name || "",
    lastName: row?.Last_Name || "",
    email: row?.Email || "",
    companyEmail: row?.Company_Email || "",
    password: row?.Password || "",
    employeeId: row?.Employee_Id || "",
    countryCode: row?.Country_Code || "+91",
    phoneNumber: row?.Phone_Number || "",
    altPhone: row?.Alt_Phone || "",
    department: row?.Department || "",
    designationId: row?.Designation_Id ? String(row.Designation_Id) : "",
    gender: row?.Gender || "",
    status: row?.Status || "Active",
    notes: row?.Notes || "",
  };
}

function buildPayload(form) {
  return {
    First_Name: form.firstName.trim(),
    Last_Name: form.lastName.trim() || null,
    Email: form.email.trim() || null,
    Company_Email: form.companyEmail.trim(),
    Password: form.password.trim(),
    Employee_Id: form.employeeId.trim(),
    Country_Code: form.countryCode.trim() || null,
    Phone_Number: form.phoneNumber.trim(),
    Alt_Phone: form.altPhone.trim() || null,
    Department: form.department.trim() || null,
    Designation_Id: Number(form.designationId),
    Gender: form.gender.trim() || null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  };
}

function validateEmployeeForm(form) {
  if (!form.firstName.trim()) {
    return "First name is required.";
  }
  if (!form.email.trim()) {
    return "Email is required.";
  }
  if (!form.companyEmail.trim()) {
    return "Company email is required.";
  }
  if (!form.employeeId.trim()) {
    return "Employee ID is required.";
  }
  const phoneError = mobileValidationMessage(form.phoneNumber, { required: true });
  if (phoneError) return phoneError;
  const altPhoneError = mobileValidationMessage(form.altPhone, { label: "Alternate phone" });
  if (altPhoneError) return altPhoneError;
  if (!form.designationId) {
    return "Designation is required.";
  }
  if (!form.password.trim()) {
    return "Password is required.";
  }
  return "";
}

function CustomerEmployeeView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [empRes, metaRes] = await Promise.all([
          axiosInstance.get(`/client/employees/${id}`),
          axiosInstance.get("/client/employee-meta").catch(() => ({ data: { data: {} } })),
        ]);
        if (!active) return;
        const data = empRes.data?.data || null;
        setEmployee(data);
        setForm(toForm(data));
        if (!data) setError("Employee not found.");
        const meta = metaRes.data?.data || {};
        setDesignations(Array.isArray(meta.designations) ? meta.designations : []);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load employee details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    const validationError = validateEmployeeForm(form);
    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/employees/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setEmployee(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Employee updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update employee.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;

    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/employees/${id}`);
      navigate("/portal/employees");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate employee.",
      });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/employees")}
        >
          <FaArrowLeft /> Back to employees
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Employee not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(employee));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to employee
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave} noValidate>
          <div className="cportal-card-head">
            <h2>Edit employee</h2>
          </div>
          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>First name <span className="cportal-required">*</span></label>
              <input
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Last name</label>
              <input value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Email <span className="cportal-required">*</span></label>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} required />
            </div>
            <div className="cportal-field">
              <label>Company email <span className="cportal-required">*</span></label>
              <input
                type="email"
                value={form.companyEmail}
                onChange={(e) => setField("companyEmail", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field cportal-field-phone">
              <label>Mobile number <span className="cportal-required">*</span></label>
              <div className="cportal-phone-row">
                <input
                  className="cportal-phone-code"
                  value={form.countryCode}
                  onChange={(e) => setField("countryCode", e.target.value)}
                />
                <input
                  value={form.phoneNumber}
                  onChange={(e) => setField("phoneNumber", sanitizeMobileInput(e.target.value))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  required
                />
              </div>
            </div>
            <div className="cportal-field">
              <label>Password <span className="cportal-required">*</span></label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                maxLength={20}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="cportal-field">
              <label>Employee ID <span className="cportal-required">*</span></label>
              <input
                value={form.employeeId}
                onChange={(e) => setField("employeeId", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Gender</label>
              <input value={form.gender} onChange={(e) => setField("gender", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Alt phone</label>
              <input
                value={form.altPhone}
                onChange={(e) => setField("altPhone", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Department</label>
              <input value={form.department} onChange={(e) => setField("department", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Designation <span className="cportal-required">*</span></label>
              <select
                value={form.designationId}
                onChange={(e) => setField("designationId", e.target.value)}
                required
              >
                <option value="">Select designation</option>
                {designations.map((d) => (
                  <option key={d.Designation_Id} value={d.Designation_Id}>
                    {d.Designation_Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {EMPLOYEE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field cportal-field--full">
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>
          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(employee));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/employees")}
      >
        <FaArrowLeft /> Back to employees
      </button>

      {message.text && (
        <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {initials(employee)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{fullName(employee)}</h1>
            <span className={statusBadgeClass(employee.Status)}>
              {displayValue(employee.Status)}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting || employee.Status === "Inactive"}
            >
              <FaTrash aria-hidden="true" />
              {deleting ? "Deactivating…" : "Delete"}
            </button>
          )}
        </div>
      </div>

      <div className="cportal-detail-grid">
        <DetailSection icon={FaUser} title="Contact information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="First name" value={employee.First_Name} />
            <DetailItem label="Last name" value={employee.Last_Name} />
            <DetailItem label="Email" value={employee.Email} />
            <DetailItem label="Company email" value={employee.Company_Email} />
            <DetailItem label="Mobile number">{phoneDisplay(employee)}</DetailItem>
            <DetailItem label="Alt phone" value={employee.Alt_Phone} />
            <DetailItem label="Password" value={employee.Password} />
          </div>
        </DetailSection>

        <DetailSection icon={FaBriefcase} title="Professional information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Employee ID" value={employee.Employee_Id} />
            <DetailItem label="Designation" value={employee.Designation} />
            <DetailItem label="Department" value={employee.Department} />
            <DetailItem label="Gender" value={employee.Gender} />
            <DetailItem label="Date of joining">{formatDate(employee.DOJ)}</DetailItem>
          </div>
        </DetailSection>
      </div>

      <DetailSection icon={FaInfoCircle} title="Other details">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Created on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(employee.Created_On)}
            </span>
          </DetailItem>
          <DetailItem label="Updated on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(employee.Updated_On)}
            </span>
          </DetailItem>
        </div>
      </DetailSection>

      <DetailSection icon={FaStickyNote} title="Notes">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Notes" value={employee.Notes} />
        </div>
      </DetailSection>
    </div>
  );
}

export default CustomerEmployeeView;
